import { AlertCircle, MessageSquare, SquarePen } from "lucide-react";
import React from "react";

import type { ChatsViewProps } from "./types";

const formatRelativeTime = (isoString: string): string => {
  const date = new Date(isoString);
  const diffMinutes = Math.round((Date.now() - date.getTime()) / 60_000);
  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const NewChatButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex flex-shrink-0 items-center gap-2 rounded-lg bg-primary-subtle px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/20 focus:outline-none focus-visible:shadow-focus"
  >
    <SquarePen size={16} />
    New chat
  </button>
);

export const ChatsView: React.FC<ChatsViewProps> = ({
  conversations,
  loading,
  error,
  activeConversationId,
  onSelectConversation,
  onNewChat,
}) => {
  if (loading && conversations.length === 0) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="h-16 animate-pulse rounded-xl border border-neutral-200 bg-neutral-100"
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[240px] flex-col items-center justify-center rounded-2xl border border-destructive-border bg-destructive-subtle p-8 text-center">
        <AlertCircle className="mb-3 text-destructive-text" size={24} />
        <h3 className="text-base font-semibold text-destructive-text">Failed to load chats</h3>
        <p className="mt-1 text-sm text-destructive-text">{error}</p>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="flex min-h-[280px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-neutral-200 bg-card p-12 text-center">
        <MessageSquare size={36} className="mb-3 text-neutral-400" />
        <h3 className="text-lg font-semibold text-neutral-900">No conversations yet</h3>
        <p className="mt-2 max-w-md text-sm text-neutral-500">
          Ask a question about this case&apos;s documents to start one.
        </p>
        <button
          type="button"
          onClick={onNewChat}
          className="mt-5 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-hover focus:outline-none focus-visible:shadow-focus"
        >
          <SquarePen size={16} />
          Start a conversation
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between rounded-xl border border-neutral-200 bg-card px-4 py-3">
        <p className="text-sm text-neutral-600">
          <span className="font-semibold text-neutral-900">{conversations.length}</span>{" "}
          conversation{conversations.length === 1 ? "" : "s"}
        </p>
        <NewChatButton onClick={onNewChat} />
      </div>

      <div className="space-y-2">
        {conversations.map((conversation) => {
          const isActive = conversation.conversation_id === activeConversationId;
          return (
            <button
              key={conversation.conversation_id}
              type="button"
              aria-current={isActive}
              onClick={() => onSelectConversation(conversation.conversation_id)}
              title={new Date(conversation.last_message_at).toLocaleString()}
              className={`flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-colors focus:outline-none focus-visible:shadow-focus ${
                isActive
                  ? "border-primary/40 bg-primary-subtle/40"
                  : "border-neutral-200 bg-card hover:border-primary/40 hover:bg-primary-subtle/40"
              }`}
            >
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary-subtle text-primary">
                  <MessageSquare size={16} />
                </span>
                <span className="truncate text-sm font-medium text-neutral-900">
                  {conversation.title || "New conversation"}
                </span>
              </div>
              <span className="flex flex-shrink-0 items-center gap-2 text-xs text-neutral-500">
                {isActive && (
                  <span className="rounded-md bg-primary px-2 py-0.5 text-2xs font-semibold uppercase tracking-micro text-white">
                    Open
                  </span>
                )}
                {formatRelativeTime(conversation.last_message_at)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
